import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Spin, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { usePostMilestoneQuery } from '@src/services/query/applang/queryHooks';
import { useAggregations } from '@src/services/queries';
import { formatMetricValue } from '@src/shared/utils/number';
import type { FilterCondition } from '@src/shared/types/user-journeys';

interface IProps {
  filters: FilterCondition[][];
  milestone?: string;
  interval: [string, string];
}

const MetricsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
`;

const MetricCard = styled.div<{ children?: React.ReactNode }>`
  background: white;
  border-radius: 4px;
  padding: 20px;
  flex: 1;
  border: 1px solid #d9d9d9;
`;

const MetricTitle = styled.div<{ children?: React.ReactNode }>`
  font-size: 16px;
  font-weight: 500;
  color: #000;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MetricValue = styled.div<{ children?: React.ReactNode }>`
  font-size: 24px;
  font-weight: 600;
  color: #333;
`;

const NAText = styled.div`
  font-size: 25px;
  font-weight: 600;
  color: rgb(0 0 0 / 25%);
`;

const milestoneDefinitions = [
  {
    id: 'count_of_records',
    unit: '#',
    description:
      'Number of times the selected milestone event occurred within the selected time period.',
  },
  {
    id: 'user_count',
    unit: '#',
    description:
      'Count of unique users (represented by User ID) who performed the milestone event within the selected time interval.',
  },
  {
    id: 'device_count',
    unit: '#',
    description:
      'Count of unique devices (represented by Client ID) that performed the milestone event within the selected time interval.',
  },
  {
    id: 'user_conversion_rate',
    unit: '%',
    description:
      'Percentage of unique users (represented by User ID) who performed the milestone event, calculated as:(Users with the event ÷ All users in the selected time interval) × 100 %.',
  },
  {
    id: 'device_conversion_rate',
    unit: '%',
    description:
      'Percentage of unique devices (represented by Client ID) that performed the milestone event, calculated as:(Devices with the event ÷ All devices in the selected time interval) × 100 %.',
  },
];

const MetricChart: React.FC<IProps> = ({ filters, milestone, interval }) => {
  const aggregations = useAggregations().data?.aggregations;

  // Filter milestone-type aggregations
  const milestoneMetrics = useMemo(() => {
    if (!aggregations) return [];
    return aggregations
      .filter((agg) => agg.type === 'milestone')
      .map((agg) => {
        const definition = milestoneDefinitions.find((m) => m.id === agg.id);
        if (!definition) return null;
        return {
          id: agg.id,
          type: 'milestone' as 'milestone' | 'pattern',
          name: agg.name,
          description: definition.description,
          unit: definition.unit,
        };
      })
      .filter((m) => m !== null);
  }, [aggregations]);

  // Build query payload with automatic caching
  const milestoneQueryPayload = useMemo(() => {
    if (!milestone || !interval || !interval[0] || !interval[1]) return undefined;

    return {
      milestone: milestone,
      time_range: interval,
      filters: filters,
      aggregations: milestoneMetrics.map((metric) => metric.id),
    };
  }, [milestone, interval, filters, milestoneMetrics]);

  const { data, isFetching } = usePostMilestoneQuery(milestoneQueryPayload);
  const metricsData = data && data.length > 0 ? data[0] : null;

  return (
    <MetricsContainer>
      {milestoneMetrics.map((metric) => (
        <MetricCard key={metric.id}>
          <MetricTitle>
            {metric.name}
            <Tooltip title={`${metric.description}`}>
              <InfoCircleOutlined style={{ marginLeft: '4px', color: '#999', fontSize: '14px' }} />
            </Tooltip>
          </MetricTitle>
          {isFetching ? (
            <Spin />
          ) : (
            <MetricValue>
              {milestone ? (
                formatMetricValue(metric, metricsData) || <NAText>NA</NAText>
              ) : (
                <NAText>NA</NAText>
              )}
            </MetricValue>
          )}
        </MetricCard>
      ))}
    </MetricsContainer>
  );
};

export default MetricChart;
